import { Injectable, inject, signal, computed, DestroyRef, effect } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TopBarModule,
  TopBarScreen,
  TOP_BAR_MODULES,
} from '../../../configs/top-bar-navigation.config';
import { DataService } from '../data/data.service';

/**
 * Reactive navigation state for the Top Bar layout.
 * Automatically resolves the current module and screen from the URL.
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationStateService {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private dataService = inject(DataService);

  /** The currently active module resolved from the URL */
  readonly currentModule = signal<TopBarModule | null>(null);

  /** The currently active screen resolved from the URL */
  readonly currentScreen = signal<TopBarScreen | null>(null);

  /** Whether the mega menu overlay is open */
  readonly megaMenuOpen = signal<boolean>(false);

  /** Display label for the current module (translation key) */
  readonly currentModuleName = computed(
    () => this.currentModule()?.translationKey ?? this.currentModule()?.name ?? ''
  );

  /** Display label for the current screen (translation key) */
  readonly currentScreenName = computed(
    () => this.currentScreen()?.translationKey ?? this.currentScreen()?.name ?? ''
  );

  /** Module icon identifier */
  readonly currentModuleIcon = computed(() => this.currentModule()?.icon ?? 'graph');

  /** Module brand colour */
  readonly currentModuleColor = computed(() => this.currentModule()?.color ?? '#6b7280');

  constructor() {
    // Resolve on initial load
    this.resolveFromUrl(this.router.url);

    // Re-resolve when active module context changes (important for detail pages)
    effect(() => {
      this.dataService.activeModule();
      this.dataService.listBreadcrumbContext();
      this.resolveFromUrl(this.router.url);
    });

    // Re-resolve on every navigation
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.resolveFromUrl(event.urlAfterRedirects);
      });
  }

  /** Toggle mega menu open/close */
  toggleMegaMenu(): void {
    this.megaMenuOpen.update((open) => !open);
  }

  /** Close the mega menu */
  closeMegaMenu(): void {
    this.megaMenuOpen.set(false);
  }

  /** Open the mega menu */
  openMegaMenu(): void {
    this.megaMenuOpen.set(true);
  }

  /**
   * Resolve the current module and screen from a URL path.
   * Matches by finding the module whose route is the best prefix match.
   * Detail pages (e.g. /declarations/details/:id) sit outside list routes,
   * so they fall back to activeModule + path heuristics.
   */
  private resolveFromUrl(url: string): void {
    const path = url.split('?')[0].split(';')[0];

    let bestModule: TopBarModule | null = null;
    let bestScreen: TopBarScreen | null = null;
    let bestMatchLength = 0;

    for (const mod of TOP_BAR_MODULES) {
      for (const screen of mod.screens) {
        const screenRoute = screen.route;
        if (path === screenRoute || path.startsWith(screenRoute + '/')) {
          if (screenRoute.length > bestMatchLength) {
            bestMatchLength = screenRoute.length;
            bestModule = mod;
            bestScreen = screen;
          }
        }
      }

      // Also check the module's root route
      if (path === mod.route || path.startsWith(mod.route + '/')) {
        if (mod.route.length > bestMatchLength) {
          bestMatchLength = mod.route.length;
          bestModule = mod;
          bestScreen = mod.screens[0] ?? null;
        }
      }
    }

    if (!bestModule) {
      const detailMatch = this.resolveDetailContext(path);
      if (detailMatch) {
        bestModule = detailMatch.module;
        bestScreen = detailMatch.screen;
      }
    }

    if (bestModule?.key === 'emcs' && (path === '/emcs' || path === '/emcs/')) {
      const urlTree = this.router.parseUrl(url);
      const queryParams = urlTree.queryParams;
      const specific = (queryParams['specificValueFields'] as string) ?? '';
      if (
        specific.includes('originTypeCode') ||
        specific.includes('Import') ||
        specific.includes('IMPORT') ||
        queryParams['tab'] === 'emcs-import' ||
        queryParams['tab'] === 'import'
      ) {
        bestScreen =
          bestModule.screens.find((s) => s.key === 'emcs-import') ?? bestScreen;
      } else if (
        specific.includes('destinationTypeCode') ||
        specific.includes('Export') ||
        specific.includes('EXPORT') ||
        queryParams['tab'] === 'emcs-export' ||
        queryParams['tab'] === 'export'
      ) {
        bestScreen =
          bestModule.screens.find((s) => s.key === 'emcs-export') ?? bestScreen;
      } else {
        bestScreen =
          bestModule.screens.find((s) => s.key === 'emcs-list') ?? bestScreen;
      }
    }

    this.currentModule.set(bestModule);
    this.currentScreen.set(bestScreen);
  }

  /**
   * Map detail/create/edit URLs that are not nested under list routes
   * (e.g. /shipments/details/:id, /declarations/details/:id) back to a module + screen.
   */
  private resolveDetailContext(
    path: string
  ): { module: TopBarModule; screen: TopBarScreen } | null {
    const find = (
      moduleKey: string,
      screenKey: string
    ): { module: TopBarModule; screen: TopBarScreen } | null => {
      const module = TOP_BAR_MODULES.find((m) => m.key === moduleKey) ?? null;
      const screen = module?.screens.find((s) => s.key === screenKey) ?? null;
      return module && screen ? { module, screen } : null;
    };

    const importOrExport = (): 'import' | 'export' => {
      const active = this.dataService.activeModule();
      if (active === 'export') {
        return 'export';
      }
      if (active === 'import') {
        return 'import';
      }
      const ctx = this.dataService.listBreadcrumbContext();
      if (ctx?.route?.includes('/export/')) {
        return 'export';
      }
      return 'import';
    };

    if (
      path.includes('/shipments/details/') ||
      path.includes('/shipments/edit/')
    ) {
      return find(importOrExport(), 'shipments');
    }

    if (
      path.includes('/declarations/details/') ||
      path.includes('/declarations/edit/') ||
      path.includes('/delivery/edit/')
    ) {
      return find(importOrExport(), 'declarations');
    }

    if (path.includes('/tickets/details/')) {
      return find('tasks', 'tasks');
    }

    if (path.includes('/emcs/details/') || path.includes('/emcs/edit/')) {
      return find('emcs', 'emcs-list');
    }

    return null;
  }
}
